<script setup lang="ts">
import { ref, computed, watch, onUnmounted, nextTick, type Component } from 'vue';

import {
  ToastClose,
  ToastDescription,
  ToastProvider,
  ToastRoot,
  ToastTitle,
  ToastViewport,
} from 'reka-ui';
import { storeToRefs } from 'pinia';
import { useToastStore } from '@/stores/utils/toast.store';
import { useLoaderStore } from '@/stores/utils/loader.store';
import type { ErrorType } from '@/types/utils/toast.types';
import { LockClosedIcon, XMarkIcon } from '@heroicons/vue/24/outline';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ExclamationCircleIcon,
} from '@heroicons/vue/24/solid';

import { Spinner } from '@boqnet/ui';
import i18n from '@/plugins/vue-i18n';

const isPaused = ref(false);

const { t } = i18n.global;

const toastStore = useToastStore();
const { toasts } = storeToRefs(toastStore);

const loaderStore = useLoaderStore();
const isDocumentUploading = computed(() => loaderStore.getLoaderByName('document-upload'));

const uploadComplete = ref(false);
let completionTimer: ReturnType<typeof setTimeout> | null = null;

watch(isDocumentUploading, (uploading, wasUploading) => {
  if (!uploading && wasUploading) {
    uploadComplete.value = true;
    completionTimer = setTimeout(() => {
      uploadComplete.value = false;
    }, 1000);
  }
});

onUnmounted(() => {
  if (completionTimer) clearTimeout(completionTimer);
});

const containerClasses: Record<ErrorType, string> = {
  error: 'border-border-border-200 bg-white',
  success: 'border-border-border-200 bg-white',
  warn: 'border-border-border-200 bg-white',
  info: 'border-border-border-200 bg-white',
};

const iconClasses: Record<ErrorType, string> = {
  error: 'text-red-600',
  success: 'text-green-600',
  warn: 'text-yellow-500',
  info: 'text-blue-600',
};

const typeIcons: Record<ErrorType, Component> = {
  success: CheckCircleIcon,
  error: ExclamationCircleIcon,
  warn: ExclamationTriangleIcon,
  info: InformationCircleIcon,
};

const progressClasses: Record<ErrorType, string> = {
  error: 'bg-red-600',
  success: 'bg-green-600',
  warn: 'bg-yellow-500',
  info: 'bg-blue-600',
};

const handleToastClick = async (toastId: string, remaining: number): Promise<void> => {
  toastStore.togglePinToast(toastId, remaining);
  await nextTick();
  (document.activeElement as HTMLElement)?.blur();
};
</script>

<template>
  <!-- Regular toasts: bottom-left -->
  <ToastProvider>
    <ToastRoot
      v-for="toast in toasts"
      v-slot="{ remaining }"
      :key="toast.id"
      :open="true"
      data-testid="toast-notification"
      :data-toast-type="toast.type"
      :duration="toast.persistent ? Infinity : (toast.savedRemaining ?? toast.timeout)"
      :class="[
        'relative flex flex-col overflow-hidden rounded-lg border shadow-md sm:w-96',
        containerClasses[toast.type],
      ]"
      @update:open="(open: boolean) => !open && toastStore.removeToast(toast.id)"
    >
      <div class="flex cursor-pointer flex-col" @click="handleToastClick(toast.id, remaining)">
        <div class="flex items-start gap-3 px-3 pt-3">
          <div class="min-w-0 flex-1">
            <ToastTitle class="type-h5 flex items-center gap-1">
              <component
                :is="typeIcons[toast.type]"
                :class="[iconClasses[toast.type], 'h-5 min-h-5 w-5 min-w-5']"
              />
              <p class="text-txt-800">{{ toast.title }}</p>
            </ToastTitle>
            <ToastDescription
              v-if="toast.description"
              class="text-shadow-txt-800 type-body-2 py-3 pb-2"
            >
              {{ toast.description }}
            </ToastDescription>
            <div>
              <p class="type-body-2 py-1">
                <template v-if="toast.persistent">
                  <span class="text-txt-700 pr-1">{{ t('toast.pinMessage.message') }}</span>
                  <span class="text-txt-800 font-medium">{{ t('toast.pinMessage.action') }}</span>
                </template>
                <template v-else>
                  <span class="text-txt-700 pr-1">
                    {{ t('toast.durationMessage.message', { duration: toast.timeout / 1000 }) }}
                  </span>
                  <span class="text-txt-800 font-medium">{{
                    t('toast.durationMessage.action')
                  }}</span>
                </template>
              </p>
            </div>
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <LockClosedIcon v-if="toast.persistent" class="mb-1.5 h-3.5 w-3.5 text-gray-400" />
            <div @click.stop>
              <ToastClose
                class="text-txt-800 transition-colors hover:opacity-60"
                aria-label="Close"
              >
                <XMarkIcon class="h-4 w-4" />
              </ToastClose>
            </div>
          </div>
        </div>
        <div
          class="h-1"
          :class="[
            toast.persistent ? 'bg-gray-400' : progressClasses[toast.type],
            !toast.persistent && !isPaused && 'transition-[width] duration-100 ease-linear',
          ]"
          :style="
            toast.persistent || isPaused
              ? {
                  width: toast.persistent ? '100%' : `${(remaining / toast.timeout) * 100}%`,
                  backgroundImage:
                    'repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(255,255,255,0.45) 4px, rgba(255,255,255,0.45) 8px)',
                }
              : { width: `${(remaining / toast.timeout) * 100}%` }
          "
        />
      </div>
    </ToastRoot>

    <ToastViewport
      class="fixed bottom-4 left-4 z-[10001] flex flex-col gap-2 outline-none"
      @pointermove="isPaused = true"
      @pointerleave="isPaused = false"
    />
  </ToastProvider>

  <!-- Document upload loading toast: top-left -->
  <ToastProvider>
    <ToastRoot
      :open="isDocumentUploading || uploadComplete"
      :duration="Infinity"
      class="border-border-border-200 relative flex flex-col overflow-hidden rounded-lg border bg-white shadow-md sm:w-96"
    >
      <div class="flex items-center gap-2 px-3 py-3">
        <CheckCircleIcon v-if="uploadComplete" class="h-5 w-5 shrink-0 text-green-600" />
        <Spinner v-else size="xs" color="blue-800" class="shrink-0" />
        <ToastTitle class="text-txt-800 type-body-1-strong">
          {{
            uploadComplete ? t('utilities.documentUploadComplete') : t('utilities.documentUpload')
          }}
        </ToastTitle>
      </div>
    </ToastRoot>

    <ToastViewport class="fixed top-4 left-4 z-[10001] flex flex-col gap-2 outline-none" />
  </ToastProvider>
</template>
